/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FILTER_KEYS,
  TABLE_KEYS
} from '@/shared/components/Table/models/Filter.model'
import { useTranslation } from 'react-i18next'
import Table from '@/shared/components/Table/Table'
import { useTableFilterStore } from '@/shared/components/Table/store/useTableFilterStore'
import { useEffect, useMemo, useRef, useState } from 'react'
import { OptionType, LanguageModel } from '@/shared/api/BaseAPI.model'
import { useIngredientsColumnsConfig } from '@/features/menu-management/Ingredients/hooks/useIngredientsColumnsConfig'
import { useIngredientsQuery } from '@/features/menu-management/Ingredients/hooks/useIngredientsQuery'
import { useIngredientsFormConfig } from '@/features/menu-management/Ingredients/hooks/useIngredientsFormConfig'
import { useSelectedLanguagesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useSelectedLanguagesQuery'
import DetailsModal from '@/shared/components/Modal/DetailsModal/DetailsModal'
import styles from '@/features/menu-management/Ingredients/IngredientsManagement.module.scss'
import { useFormDirtyStore } from '@/shared/store/useFormDirtyStore'
import { formatPriceForInput, majorToMinor } from '@/shared/utils/price'
import { translatedName } from '@/shared/utils/translatedName'

const mapIngredientsDataToPayload = (
  data: any,
  selectedLanguagesList: LanguageModel[]
) => ({
  translations: selectedLanguagesList.map(lang => ({
    languageCode: lang.languageCode,
    name: data.ingredientName[lang.languageCode]
  })),
  allergenIds: data.ingredientAllergens.map(
    (allergen: OptionType) => allergen.value
  ),
  pricePerUnit: {
    unitId: data.ingredientUnit.value,
    price: majorToMinor(data.price) ?? 0
  },
  quantity: Number(data.quantity),
  unitId: data.ingredientUnit.value
})

function IngredientsManagement() {
  const { t } = useTranslation()
  const [ingredientId, setIngredientId] = useState<string>()
  const configuredIngredientIdRef = useRef<string>()

  const columnsModel = ['name', 'allergens', 'quantity', 'unit']

  const resetIsDirty = useFormDirtyStore(s => s.reset)

  useEffect(() => resetIsDirty(), [])

  const filtersList = [FILTER_KEYS.ALLERGENS_FILTER]

  const {
    ingredientsList,
    ingredientListLoading,
    allergensList,
    unitsList,
    deleteIngredients,
    postIngredient,
    putIngredient
  } = useIngredientsQuery(ingredientId)

  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { defaultLanguageOption, selectedLanguagesList } =
    useSelectedLanguagesQuery(currentLanguage)

  const { filters } = useTableFilterStore()
  const filterLanguageCode =
    filters.ingredients?.[FILTER_KEYS.LANGUANGE_FILTER] || defaultLanguageOption
  const selectedAllergensLabels = (
    filters.ingredients?.[FILTER_KEYS.ALLERGENS_FILTER] || []
  ).map(filter => filter.label)

  const lang = (filterLanguageCode?.value || 'en').toLowerCase()
  const selectedAllergensLabelsSet = new Set(selectedAllergensLabels)

  const unitsById = new Map((unitsList.data ?? []).map(u => [u.id, u]))

  const allergensById = new Map((allergensList.data ?? []).map(a => [a.id, a]))

  const getNameByLanguage = (
    translations?: { languageCode: string; name: string }[]
  ) =>
    translations?.find(t => t.languageCode?.toLowerCase() === lang)?.name || ''

  const mappedIngredientsList = (ingredientsList ?? [])
    .map(ingredient => {
      const unit = unitsById.get(ingredient.unitId)
      const allergenNames = (ingredient.allergenIds ?? []).reduce<
        { id: string; label: string; iconUrl?: string }[]
      >((acc, aid) => {
        const allergen = allergensById.get(aid)
        if (!allergen) {
          return acc
        }
        acc.push({
          id: allergen.id,
          label: getNameByLanguage(allergen.translations),
          iconUrl: allergen.iconUrl || undefined
        })
        return acc
      }, [])

      return {
        id: ingredient.id,
        name: getNameByLanguage(ingredient.translations),
        allergens: allergenNames,
        quantity: ingredient.quantity,
        unit: unit ? getNameByLanguage(unit.translations) : ''
      }
    })
    .filter(ingredient => {
      if (!selectedAllergensLabels.length) {
        return true
      }
      const rowAllergenLabels = ingredient.allergens.map(
        allergen => allergen.label
      )
      return rowAllergenLabels.some(label =>
        selectedAllergensLabelsSet.has(label)
      )
    })

  const handleEditIngredient = (id: string) => {
    setIngredientId(id)
  }

  const handleOnDeleteItem = (id: string) => {
    deleteIngredients([id])
  }

  const columns = useIngredientsColumnsConfig(
    handleEditIngredient,
    handleOnDeleteItem
  )

  const initialFormConfig = useIngredientsFormConfig()
  const [formConfig, setFormConfig] = useState(initialFormConfig)

  const [isModalOpen, setIsModalOpen] = useState(false)

  const defaultValues = useMemo(
    () => ({
      ingredientLanguage: defaultLanguageOption as OptionType,
      ingredientName: selectedLanguagesList.reduce(
        (acc, lang) => {
          acc[lang.languageCode] = ''
          return acc
        },
        {} as Record<string, string>
      ),
      allergenIds: [] as string[],
      quantity: '',
      pricePerUnit: {
        price: '',
        unitId: ''
      },
      unitId: '' as string
    }),
    [defaultLanguageOption, selectedLanguagesList]
  )

  const handleAddIngredient = () => {
    const onSubmit = (data: any) => {
      const postData = mapIngredientsDataToPayload(data, selectedLanguagesList)
      postIngredient(postData)
      resetIsDirty()
      setIsModalOpen(false)
    }

    setFormConfig({ ...initialFormConfig, defaultValues, onSubmit })
    setIsModalOpen(true)
  }

  useEffect(() => {
    if (ingredientId && ingredientId !== configuredIngredientIdRef.current) {
      const ingredient = ingredientsList?.find(i => i.id === ingredientId)
      if (!ingredient) return

      configuredIngredientIdRef.current = ingredientId

      const onSubmit = (data: any) => {
        const postData = mapIngredientsDataToPayload(
          data,
          selectedLanguagesList
        )
        putIngredient(postData)
        resetIsDirty()
        setIsModalOpen(false)
      }

      const defaultIngredientNames = ingredient.translations.reduce(
        (acc, item) => {
          acc[item.languageCode] = item.name
          return acc
        },
        {} as Record<string, string>
      )

      const defaultAllergens = (allergensList.data ?? [])
        .filter(a => ingredient.allergenIds.includes(a.id))
        .map(a => ({
          value: a.id,
          label: translatedName(
            a.translations,
            defaultLanguageOption?.value as string | undefined
          )
        }))

      const defaultUnit = unitsList.data?.find(u => u.id === ingredient.unitId)
      const defaultUnitOption = defaultUnit
        ? {
            value: defaultUnit.id,
            label: translatedName(
              defaultUnit.translations,
              defaultLanguageOption?.value as string | undefined
            )
          }
        : null

      const newDefaultValues = {
        ingredientLanguage: defaultLanguageOption,
        ingredientName: defaultIngredientNames,
        ingredientAllergens: defaultAllergens,
        price: formatPriceForInput(ingredient.pricePerUnit?.price),
        quantity: ingredient.quantity ?? '',
        ingredientUnit: defaultUnitOption
      }

      setFormConfig({
        ...initialFormConfig,
        defaultValues: newDefaultValues,
        onSubmit
      })
      setIsModalOpen(true)
    }
  }, [
    ingredientId,
    ingredientsList,
    allergensList.data,
    unitsList.data,
    putIngredient,
    initialFormConfig,
    defaultLanguageOption,
    selectedLanguagesList,
    resetIsDirty
  ])

  const handleOnDeleteItems = (itemIds: string[]) => {
    deleteIngredients(itemIds)
  }

  const handleOnCloseModal = () => {
    configuredIngredientIdRef.current = undefined
    setIngredientId(undefined)
    setIsModalOpen(false)
  }

  return (
    <div className={styles.ingredientsTableWrapper}>
      <Table
        columns={columns}
        columnsModel={columnsModel}
        filtersList={filtersList}
        tableKey={TABLE_KEYS.INGREDIENTS}
        initialRows={mappedIngredientsList || []}
        filterModel={{ items: [] }}
        handleOnDeleteItems={handleOnDeleteItems}
        handleOnAddItem={handleAddIngredient}
        isLoading={ingredientListLoading}
      />
      <DetailsModal
        title={t('INGREDIENTS_DETAILS_MODAL.TITLE')}
        formConfig={formConfig}
        onClose={handleOnCloseModal}
        isOpen={isModalOpen}
      />
    </div>
  )
}

export default IngredientsManagement
